import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import handleOpenSweetAlert from '../../handleOpenSweetAlert';
import { useSession } from '../../hooks/useSession';
import { getFacturasPorOC, sincronizarOC, guardarSolicitudPago, reclasificarSolicitud, actualizarPreferenciaProveedor, getDetalleOC, getPreferenciaProveedor } from '../../../services/modalSolicitudesService';
import * as yup from 'yup';

const documentos = [
  { name: 'requi', label: 'Requisicion' },
  { name: 'ordenCompra', label: 'Orden Compra' },
  { name: 'caratulaBancaria', label: 'Carátula bancaria' },
  { name: 'facturaa', label: 'Complemento Factura' },
  { name: 'xml', label: 'XML' },
  { name: 'cartaBancariamx', label: 'Carta bancaria (M.N.)' },
  { name: 'estadoCuentamx', label: 'Estado de cuenta (M.N.)' },
  { name: 'cartaBancariaus', label: 'Carta bancaria (dólares)' },
  { name: 'estadoCuentaus', label: 'Estado de cuenta (dólares)' },
  { name: 'embarque', label: 'Aviso embarque' },
  { name: 'entradaAlmacen', label: 'Entrada Almacén (RIM)' },
  { name: 'importacion', label: 'Pedimento importación' },
  { name: 'dossier', label: 'Dossier de Calidad' },
  { name: 'reporteFotografico', label: 'Reporte Fotográfico' },
  { name: 'estimacion', label: 'Estimación' },
  { name: 'manifiesto', label: 'Manifiesto' },
];

function parseMoney(value) {
  if (value == null) return NaN;
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
  const s = String(value).trim().replace(/[^\d.,-]/g, '').replace(/,/g, '');
  if (!s || s === '-' || s === '.' || s === '-.') return NaN;
  return Number(s);
}

const initialFormData = {
  TITULO: '', SUBTITULO: '', TOTALOC: '', FACTURA: '', FECHA: '', FECHAEMISION: '',
  fechaVencimientoPrecio: '', PROVEEDOR: '', proveedorNombre: '', proveedorRfc: '',
  banco: '', cuenta: '', clabe: '', swift: '', moneda: 'MXN', aba: '',
  conceptoPago: '', observaciones: '', numeroEconomico: '', adjuntos: [],
  documentos: documentos.reduce((acc, doc) => ({ ...acc, [doc.name]: false }), {}),
  facturasSeleccionadas: [],
  tipoPago: '',
  fechaCompromiso: '',
};

const cleanSerie = (v) => {
  const s = (v ?? '').toString().trim();
  if (!s) return '';
  const low = s.toLowerCase();
  if (s === '0' || low === 'null' || low === 'undefined') return '';
  return s;
};

const foliosFromRaw = (raw) => {
  if (raw == null || raw === 0) return [];
  const s = raw.toString().trim();
  if (!s || s === '0' || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') return [];
  return s.split(',').map(x => x.trim()).filter(x => x && x !== '0' && x.toLowerCase() !== 'null' && x.toLowerCase() !== 'undefined');
};

const buildFacturaIdentificador = ({ serie, folio, uuidBase }) => {
  const s = cleanSerie(serie);
  const f = (folio ?? '').toString().trim();
  const fLow = f.toLowerCase();
  if (f && f !== '0' && fLow !== 'null' && fLow !== 'undefined') return `${s}${f}`;
  const u = (uuidBase ?? '').toString().trim();
  if (u) return `UUID:${u}`;
  return '';
};

// ... Same helper functions from the original script ...
const getMonedaTipo = (divisaRaw) => {
  const d = (divisaRaw || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().trim();
  if (d.includes('USD') || d.includes('DOLAR') || d.includes('DLL')) return 'USD';
  if (d.includes('MXN') || d.includes('PESO') || d.includes('M.N')) return 'MXN';
  return 'MXN';
};

const normalizeCondicionCredito = (raw) => {
  const s = (raw || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  if (!s) return 'OTRO';
  if (s.includes('contado')) return 'CONTADO';
  if (s.includes('credito') || s.includes('anticipo') || s.includes('estimacion')) return 'CREDITO';
  return 'OTRO';
};

const normalizeDivisaEtiqueta = (divisa) => {
  const d = (divisa || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();
  if (d === 'USD' || d.includes('DOLAR')) return 'DÓLARES';
  if (d === 'MXN' || d.includes('PESO') || d === 'MN' || d === 'M.N' || d.includes('MONEDA NACIONAL')) return 'PESOS';
  return divisa || 'la divisa indicada';
};

export const useModalSolicitudesController = (
  open, onClose, rowData, onSaved, 
  initialFiles = [], 
  isResend = false,
  guidedMode = false,
  correctionMetadata = null,
  reclassificationOnly = false
) => {
  const { loggedInUsername } = useSession();

  const [formData, setFormData] = useState(initialFormData);
  const [maxTotalOC, setMaxTotalOC] = useState(0);
  const [formattedTotalOC, setFormattedTotalOC] = useState('');
  const [spinnerMessage, setSpinnerMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [updatingOC, setUpdatingOC] = useState(false);
  const [numeroEconomicoBloqueado, setNumeroEconomicoBloqueado] = useState(false);

  const [combinedErrors, setCombinedErrors] = useState({});
  const [totalOCError, setTotalOCError] = useState('');

  const [facturaOptionsRemote, setFacturaOptionsRemote] = useState([]);
  const [loadingFacturas, setLoadingFacturas] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', sev: 'success' });
  const [currentRowData, setCurrentRowData] = useState(null);

  const notify = useCallback((msg, sev = 'success') => setSnackbar({ open: true, msg, sev }), []);

  const activeRowData = currentRowData || rowData;

  useEffect(() => {
    setCurrentRowData(open && rowData ? rowData : null);
  }, [open, rowData]);

  const condicionCreditoRaw = activeRowData?.PAGO || activeRowData?.condicionesCredito || '';
  const condicionCredito = useMemo(() => normalizeCondicionCredito(condicionCreditoRaw), [condicionCreditoRaw]);
  const monedaTipo = useMemo(() => getMonedaTipo(activeRowData?.DIVISA || ''), [activeRowData]);
  
  // 🔹 Estado para el interruptor de "Requiere Factura"
  const [requiereFacturaAntesPago, setRequiereFacturaAntesPago] = useState(false);
  const requiereFacturaRef = useRef(requiereFacturaAntesPago);
  useEffect(() => { requiereFacturaRef.current = requiereFacturaAntesPago; }, [requiereFacturaAntesPago]);

  // 🔹 Estado para control visual de envío de correo al autorizador (Solo Lectura)
  const [configNotificarAutorizador, setConfigNotificarAutorizador] = useState(false);

  // Inicializar estado consultando al servidor para tener el valor más reciente
  useEffect(() => {
    const fetchPreferencia = async () => {
      const rfc = activeRowData?.RFC || activeRowData?.proveedorRfc || activeRowData?.rfc || activeRowData?.PROVEEDOR_RFC;
      const empId = activeRowData?.EMPRESAID || activeRowData?.empresaId || activeRowData?.IDEMPRESA;
      
      if (open && rfc && empId) {
        try {
          const res = await getPreferenciaProveedor(rfc, empId);
          if (res.ok) {
            setRequiereFacturaAntesPago(res.requiereFactura);
          }
        } catch (err) {
          console.error("Error al obtener preferencia inicial:", err);
          // Fallback al valor que ya traía el rowData si falla la consulta
          setRequiereFacturaAntesPago(Number(activeRowData.FACTURA_ANTES_PAGO) === 1);
        }
      } else if (activeRowData) {
        setRequiereFacturaAntesPago(Number(activeRowData.FACTURA_ANTES_PAGO) === 1);
      }
    };

    fetchPreferencia();
  }, [activeRowData, open]);

  const ocEnCero = useMemo(() => {
    const n = Number(maxTotalOC || 0);
    return !Number.isFinite(n) || n <= 0;
  }, [maxTotalOC]);

  const formatCurrency = useCallback((value) => {
    const num = parseFloat(value);
    return isNaN(num) ? '' : '$' + num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }, []);

  const parseCurrency = useCallback((value) => {
    const num = parseFloat((value || '').toString().replace(/[^0-9.-]/g, ''));
    return isNaN(num) ? '' : num.toString();
  }, []);

  const loadFacturasFromDB = useCallback(async (customRowData = null) => {
    const target = customRowData || activeRowData;




    if (!target?.IDDATOSERP) {
      console.warn("DEBUG - No se encontró IDDATOSERP");
      return;
    }

    try {
      setLoadingFacturas(true);
      setFacturaOptionsRemote([]);

      const res = await getFacturasPorOC(target.IDDATOSERP);
      let rows = res?.facturas || [];


      // 🔥 PLAN B: Si no hay facturas en el detalle, pero sí en la cabecera (vwpagosaut)
      const folioCabecera = target.FOLIO_FACT || target.FOLIO_FACTURA;
      if (rows.length === 0 && folioCabecera) {

        rows = [{
          UUID: target.UUID_ERP || target.UUID || `HEADER-${target.IDDATOSERP}`,
          SERIE_FACT: target.SERIE_FACT || '',
          FOLIO_FACT: folioCabecera,
          FECHA_FACT: target.FECHA_FACT,
          CANCELADA: 0
        }];
      }

      const opts = rows.flatMap((r, idxRow) => {
        const fecha = (r.FECHA_FACT || '').toString().slice(0, 10);
        const cancelada = Number(r.CANCELADA) === 1;
        const uuidBase = (r.UUID ?? '').toString().trim();
        const serieERP = cleanSerie(r.SERIE_FACT);
        const folios = foliosFromRaw(r.FOLIO_FACT);

        if (folios.length > 0) {
          return folios.map((folio, i) => {
            const idForUuid = uuidBase || `${target.IDDATOSERP}__${idxRow}`;
            const uuid = `${idForUuid}__${i}`;
            const identificador = buildFacturaIdentificador({ serie: serieERP, folio, uuidBase: idForUuid });
            return { uuid, identificador, serieERP, folioERP: folio, uuidBase: idForUuid, fecha, cancelada };
          });
        }

        const idForUuid = uuidBase || `${target.IDDATOSERP}__${idxRow}`;
        return [{
          uuid: `${idForUuid}__NF`,
          identificador: buildFacturaIdentificador({ serie: '', folio: '', uuidBase: idForUuid }) || `UUID:${idForUuid}`,
          serieERP,
          folioERP: '',
          uuidBase: idForUuid,
          fecha,
          cancelada,
        }];
      });

      setFacturaOptionsRemote(opts);
      
      // Si el ERP ya tiene facturas, obligamos a que el flujo sea "con factura"
      if (opts.length > 0) {
        setRequiereFacturaAntesPago(true);
      }

      if (requiereFacturaRef.current && opts.length === 1) {
        setFormData((prev) => {
          if ((prev.facturasSeleccionadas || []).length > 0) return prev;
          return {
            ...prev,
            facturasSeleccionadas: [{ ...opts[0], pdfFile: null, xmlFile: null }]
          };
        });
      }
    } catch (e) {
      console.error("DEBUG - Error en loadFacturasFromDB:", e);
      setFacturaOptionsRemote([]);
    } finally {
      setLoadingFacturas(false);
    }
  }, [open, activeRowData]);

  /**
   * Refresca los datos del modal consultando la DB (vwpagosaut)
   */
  const refreshModalData = useCallback(async () => {
    const folio = activeRowData?.FOLIO;
    const empresaId = activeRowData?.EMPRESAID;
    if (!folio || !empresaId) return;

    try {
      const resp = await getDetalleOC({ folio, empresaId });
      if (resp?.ok && resp.data) {
        const newData = resp.data;
        
        // 1. Actualizar montos
        const pVal = parseMoney(newData.PENDIENTE ?? newData.RESTANTE);
        const tVal = parseMoney(newData.TOTAL_OC ?? newData.TOTAL);
        const amountFromRow = parseMoney(activeRowData?.MONTOSOLICITADO || activeRowData?.MONTO);
        
        // Si el ERP devuelve 0 pero tenemos un monto en la fila (Corrección), usamos el de la fila
        let amount = (pVal === 0 || isNaN(pVal)) ? tVal : pVal;
        if ((amount === 0 || isNaN(amount)) && !isNaN(amountFromRow) && amountFromRow > 0) {
          amount = amountFromRow;
        }

        setMaxTotalOC(amount);

        // 2. Actualizar preferencia de factura
        setRequiereFacturaAntesPago(Number(newData.FACTURA_ANTES_PAGO) === 1);

        // 2b. Actualizar configuración de envío de correos
        setConfigNotificarAutorizador(!!resp.configNotificarAutorizador);

        // 3. Mapear y actualizar campos en el formulario
        // Aseguramos que los campos de la DB se mapeen a lo que espera la interfaz
        const mappedData = {
          ...activeRowData,
          ...newData,
          FOLIO: newData.FOLIO_OC || newData.FOLIO,
          CENTROCOSTOS: newData.NOMBRE_CC || activeRowData?.NOMBRE_CC || activeRowData?.CENTRO_COSTOS || activeRowData?.CENTROCOSTOS || newData.CENTRO_COSTOS || newData.CENTROCOSTOS || newData.CENTROCOSTO,
          NUMEROCC: newData.NUMERO_CC || newData.NUMEROCC || activeRowData?.NUMEROCC || activeRowData?.NUMERO_CC,
          DEPARTAMENTO: newData.DEPARTAMENTO || newData.DEP_ROW || newData.NOMBRE_DEPARTAMENTO || activeRowData?.DEPARTAMENTO,
          PROVEEDOR: newData.PROVEEDOR || newData.PROV_ROW || newData.NOMBRE_PROVEEDOR || activeRowData?.PROVEEDOR,
          EMPRESA: newData.EMPRESA || newData.NOMBRE_EMPRESA || activeRowData?.EMPRESA,
        };

        setCurrentRowData(mappedData);

        setFormData(prev => ({
          ...prev,
          ...mappedData,
          // 🛡️ Protegemos los textos originales para que el ERP no los borre si ya tenemos algo
          conceptoPago: (isResend || guidedMode) ? (prev.conceptoPago || mappedData.conceptoPago || '') : (mappedData.conceptoPago || ''),
          observaciones: (isResend || guidedMode) ? (prev.observaciones || mappedData.observaciones || '') : (mappedData.observaciones || ''),
          TOTALOC: amount.toString(),
          SUBTITULO: `Generar solicitud de pago de O.C. ${mappedData.FOLIO} por ${formatCurrency(mappedData.TOTAL)} ${normalizeDivisaEtiqueta(mappedData.DIVISA)} al proveedor ${mappedData.PROVEEDOR} (CC: ${mappedData.CENTROCOSTOS} • ${mappedData.EMPRESA})`
        }));

        setFormattedTotalOC(formatCurrency(amount));

        // 4. Refrescar facturas usando los nuevos datos
        await loadFacturasFromDB(mappedData);


      }
    } catch (err) {
      console.error('❌ Error al refrescar datos del modal:', err);
    }
  }, [activeRowData, loadFacturasFromDB, formatCurrency, setRequiereFacturaAntesPago, setMaxTotalOC, setFormData, setFormattedTotalOC, setConfigNotificarAutorizador]);

  useEffect(() => {
    if (open) {
      loadFacturasFromDB(rowData);
      refreshModalData();
    }
  }, [open, rowData]);

  // UX: Si hay facturas o se requiere factura, la fecha de vencimiento precio se auto-asigna a 1 año a futuro
  useEffect(() => {
    if (requiereFacturaAntesPago || formData.facturasSeleccionadas?.length > 0) {
      const nextYear = new Date();
      nextYear.setFullYear(nextYear.getFullYear() + 1);
      const nextYearStr = nextYear.toISOString().split('T')[0];
      
      if (formData.fechaVencimientoPrecio !== nextYearStr) {
        setFormData(prev => ({
          ...prev,
          fechaVencimientoPrecio: nextYearStr
        }));
      }
    }
  }, [requiereFacturaAntesPago, formData.facturasSeleccionadas?.length, formData.fechaVencimientoPrecio, setFormData]);

  // Ref para rastrear qué registro está cargado actualmente y evitar resets infinitos
  const loadedRowRef = useRef(null);

  useEffect(() => {
    if (!open || !rowData) {
        loadedRowRef.current = null;
        return;
    }

    const rowId = rowData.ID || rowData.folioOC || rowData.FOLIO;
    if (loadedRowRef.current === rowId) return;
    loadedRowRef.current = rowId;

    if (isResend || guidedMode) {
        const montoRaw = rowData.MONTOSOLICITADO || rowData.MONTO || '0';
        const totalRaw = rowData.TOTAL || rowData.TOTAL_OC || '0';
        const numericTotal = typeof montoRaw === 'number' ? montoRaw : parseFloat(montoRaw.toString().replace(/[^0-9.-]/g, '')) || 0;
        const totalOC = typeof totalRaw === 'number' ? totalRaw : parseFloat(totalRaw.toString().replace(/[^0-9.-]/g, '')) || 0;

        const folio = rowData.FOLIO || rowData.FOLIO_ORDENCOMPRA || '';
        const proveedor = rowData.proveedorNombre || rowData.PROVEEDOR || '';
        const moneda = rowData.DIVISA || 'MXN';

        let titulo = 'SOLICITAR PAGO';
        let subtitulo = `Generar solicitud de pago de O.C. ${folio} por ${formatCurrency(totalOC)} ${normalizeDivisaEtiqueta(moneda)} al proveedor ${proveedor}`;

        if (isResend) {
            titulo = 'REENVIAR SOLICITUD';
            subtitulo = `Reenviar solicitud de pago de O.C. ${folio} por ${formatCurrency(totalOC)} ${normalizeDivisaEtiqueta(moneda)} al proveedor ${proveedor}`;
        }
        if (guidedMode) {
             titulo = 'CORRECCIÓN GUIADA';
             subtitulo = `Corrigiendo solicitud de O.C. ${folio}. Motivo: ${correctionMetadata?.motivo || 'N/A'}`;
        }

        setFormData((prev) => {
            const baseDocs = documentos.reduce((acc, doc) => ({ 
                ...acc, 
                [doc.name]: !!rowData.documentos?.[doc.name] 
            }), {});

            // Map initialFiles to internal adjuntos state structure
            const mappedAdjuntos = (initialFiles || []).map(f => ({
                file: { name: f.name || f.filename, id: f.id, serverSide: true }, 
                type: f.category || ''
            }));

            return ({
                ...prev,
                ...rowData,
                TITULO: titulo,
                SUBTITULO: subtitulo,
                TOTALOC: numericTotal.toString(),
                conceptoPago: rowData.CONCEPTOPAGO || rowData.conceptoPago || '',
                observaciones: rowData.OBSERVACIONES || rowData.observaciones || '',
                numeroEconomico: rowData.NUMERO_ECONOMICO || rowData.numeroEconomico || '',
                documentos: baseDocs,
                adjuntos: mappedAdjuntos,
                facturasSeleccionadas: [],
            });
        });

        setFormattedTotalOC(formatCurrency(numericTotal));
        setMaxTotalOC(totalOC > 0 ? totalOC : numericTotal);
    } else {
        const pVal = parseMoney(rowData.PENDIENTE ?? rowData.RESTANTE);
        const tVal = parseMoney(rowData.TOTAL_OC ?? rowData.TOTAL);
        const numericTotal = (pVal === 0 || isNaN(pVal)) ? tVal : pVal;

        // Fallbacks para el subtítulo inicial
        const ccName = rowData.CENTRO_COSTOS || rowData.NOMBRE_CC || rowData.CENTROCOSTOS || rowData.CENTROCOSTO || rowData.NOMBRE_CENTRO_COSTOS || rowData.nombre_cc || rowData.NUMEROCC || rowData.NUMERO_CC || 'S/N';
        const empName = rowData.EMPRESA || rowData.NOMBRE_EMPRESA || rowData.empresa || 'S/N';
        const provName = rowData.PROVEEDOR || rowData.PROV_ROW || rowData.NOMBRE_PROVEEDOR || rowData.proveedor || 'S/N';

        const subtitulo = `Generar solicitud de pago de O.C. ${rowData.FOLIO} por ${rowData.TOTAL} ${normalizeDivisaEtiqueta(rowData.DIVISA)} al proveedor ${provName} (CC: ${ccName} • ${empName})`;

        const numeroEconomicoFromRow = (rowData.NUMERO_ECONOMICO ?? '').toString().trim();

        setFormData((prev) => {
            const baseDocs = documentos.reduce((acc, doc) => ({ ...acc, [doc.name]: !!rowData.documentos?.[doc.name] }), {});
            return ({
                ...prev,
                ...rowData,
                SUBTITULO: subtitulo,
                TITULO: 'SOLICITAR PAGO',
                TOTALOC: numericTotal.toString(),
                documentos: baseDocs,
                proveedorNombre: rowData.PROVEEDOR,
                numeroEconomico: numeroEconomicoFromRow,
                adjuntos: [],
                facturasSeleccionadas: [],
            });
        });

        setNumeroEconomicoBloqueado(numeroEconomicoFromRow !== '');
        setFormattedTotalOC(formatCurrency(numericTotal));
        setMaxTotalOC(numericTotal);
    }

    setTotalOCError('');
    setCombinedErrors({});
  }, [open, rowData, formatCurrency, initialFiles, isResend, guidedMode, correctionMetadata]);

  const handleActualizarOC = useCallback(async () => {
    const folio = activeRowData?.FOLIO;
    const empresaId = activeRowData?.EMPRESAID;
    if (!folio || !empresaId) return;

    try {
      setUpdatingOC(true);
      setLoading(true);
      setSpinnerMessage(`Actualizando OC ${folio} desde ERP...`);

      const resp = await sincronizarOC({
        modo: 'folio',
        folioOC: String(folio),
        serieOC: activeRowData?.SERIE_OC || '',
        empresas: [Number(empresaId)],
      });

      if (resp?.ok) {
        notify(`OC ${folio} actualizada correctamente.`, 'success');
        
        // 🔹 Refrescamos los datos del modal inmediatamente
        await refreshModalData();

        if (typeof onSaved === 'function') onSaved();
      } else {
        notify(resp?.mensaje || 'No se pudo actualizar la OC.', 'error');
      }
    } catch (err) {
      notify(err.response?.data?.mensaje || 'Error al actualizar la OC.', 'error');
    } finally {
      setLoading(false);
      setUpdatingOC(false);
    }
  }, [activeRowData, notify, onSaved, refreshModalData]);

  const handleDialogClose = useCallback(() => {
    try { document.activeElement?.blur?.(); } catch (_) { }
    setTimeout(() => {
      onClose();
      setLoading(false);
      setFormData(initialFormData);
      setFormattedTotalOC('');
      setTotalOCError('');
      setCombinedErrors({});
      setMaxTotalOC(0);
      setNumeroEconomicoBloqueado(false);
      setFacturaOptionsRemote([]);
    }, 50);
  }, [onClose]);

  const handleFileChange = useCallback((e) => {
    e.preventDefault();
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files).map(file => ({
        file,
        type: ''
      }));
      setFormData((prev) => ({
        ...prev,
        adjuntos: [...prev.adjuntos, ...newFiles],
      }));
    }
    setCombinedErrors(prev => ({ ...prev, adjuntos: null }));
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles = Array.from(e.dataTransfer.files).map(file => ({
        file,
        type: ''
      }));
      setFormData((prev) => ({
        ...prev,
        adjuntos: [...prev.adjuntos, ...newFiles],
      }));
    }
    setCombinedErrors(prev => ({ ...prev, adjuntos: null }));
  }, []);

  const handleRemoveFile = useCallback((idx) => {
    setFormData((prev) => {
      const newAdjuntos = [...prev.adjuntos];
      newAdjuntos.splice(idx, 1);
      return { ...prev, adjuntos: newAdjuntos };
    });
  }, []);

  const handleUpdateFileType = useCallback((idx, newType) => {
    setFormData((prev) => {
      const newAdjuntos = [...prev.adjuntos];
      newAdjuntos[idx] = { ...newAdjuntos[idx], type: newType };
      return { ...prev, adjuntos: newAdjuntos };
    });
  }, []);

  const handleCheckboxChange = useCallback((e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      documentos: {
        ...prev.documentos,
        [name]: checked,
      },
    }));
    setCombinedErrors((prev) => {
      const updated = { ...prev };
      delete updated.documentos;
      return updated;
    });
  }, []);

  useEffect(() => {
    setFormData((prev) => {
      let changed = false;
      const newDocs = { ...prev.documentos };

      // Carátula Bancaria a partir de tags en adjuntos
      const hasCaratula = prev.adjuntos.some(a => a.type === 'CARATULA' || a.type === 'DATOS BANCARIOS');
      if (hasCaratula && !newDocs.caratulaBancaria) {
        newDocs.caratulaBancaria = true;
        changed = true;
      } else if (!hasCaratula && newDocs.caratulaBancaria) {
        newDocs.caratulaBancaria = false;
        changed = true;
      }

      // Orden de Compra a partir de tags en adjuntos
      const hasOC = prev.adjuntos.some(a => a.type === 'OC');
      if (hasOC && !newDocs.ordenCompra) {
        newDocs.ordenCompra = true;
        changed = true;
      } else if (!hasOC && newDocs.ordenCompra) {
        newDocs.ordenCompra = false;
        changed = true;
      }

      // Factura (PDF/XML) - Check if tagged manually as FACTURA OR if uploaded via specific boxes
      const hasTaggedFac = prev.adjuntos.some(a => a.type === 'FACTURA');
      const hasPdf = prev.facturasSeleccionadas?.some(f => f.pdfFile);
      if ((hasPdf || hasTaggedFac) && !newDocs.facturaa) {
        newDocs.facturaa = true;
        changed = true;
      } else if (!hasPdf && !hasTaggedFac && newDocs.facturaa) {
        newDocs.facturaa = false;
        changed = true;
      }
      
      const hasXml = prev.facturasSeleccionadas?.some(f => f.xmlFile);
      if ((hasXml || hasTaggedFac) && !newDocs.xml) {
        newDocs.xml = true;
        changed = true;
      } else if (!hasXml && !hasTaggedFac && newDocs.xml) {
        newDocs.xml = false;
        changed = true;
      }

      if (changed) {
        return { ...prev, documentos: newDocs };
      }
      return prev;
    });
  }, [formData.adjuntos, formData.facturasSeleccionadas]);

  /**
   * Maneja el cambio del interruptor de "Requiere Factura"
   * Ofrece guardar permanentemente o solo para esta vez.
   */
  const handleTogglePreferenciaProveedor = useCallback(async (event) => {
    const nuevoEstado = !requiereFacturaAntesPago;

    // Si hay facturas cargadas, bloqueamos el cambio según la nueva regla
    if (facturaOptionsRemote.length > 0) {
      notify('No se puede cambiar la preferencia porque ya existen facturas asociadas en el ERP.', 'info');
      return;
    }

    const res = await handleOpenSweetAlert(
      `¿Deseas ${nuevoEstado ? 'activar' : 'desactivar'} el requerimiento de factura para esta solicitud?`,
      'question',
      {
        showCancelButton: true,
        confirmButtonText: 'Sí, cambiar',
        cancelButtonText: 'No',
        confirmButtonColor: '#2196f3',
      }
    );
    
    if (res.isConfirmed) {
      setRequiereFacturaAntesPago(nuevoEstado);
      notify(`Requerimiento de factura ${nuevoEstado ? 'activado' : 'desactivado'} temporalmente.`, 'info');
    }
  }, [requiereFacturaAntesPago, notify, facturaOptionsRemote]);

  const handleConfirmGuardar = useCallback(async () => {
    const errs = {};
    if (ocEnCero && !reclassificationOnly) {
      notify('La OC está en $0. Actualiza desde ERP antes de guardar.', 'warning');
      return;
    }

    // Minimum Classification enforcement: At least OC, Factura and Caratula must be present.
    if (guidedMode && correctionMetadata?.reclasificar) {
        const hasOC = (formData.adjuntos || []).some(a => a.type === 'OC');
        const hasFac = (formData.adjuntos || []).some(a => a.type === 'FACTURA');
        const hasCB = (formData.adjuntos || []).some(a => a.type === 'CARATULA' || a.type === 'DATOS BANCARIOS');
        
        if (!hasOC || !hasFac || !hasCB) {
            const missing = [];
            if (!hasOC) missing.push('OC');
            if (!hasFac) missing.push('Factura');
            if (!hasCB) missing.push('Carátula');
            notify(`Para continuar, debes clasificar los documentos clave: ${missing.join(', ')}`, 'warning');
            return;
        }
    }

    // DUPLICATE FILES CHECK
    const factSel = formData.facturasSeleccionadas || [];
    const factFiles = new Set();
    factSel.forEach(f => {
       if (f.pdfFile) factFiles.add(`${f.pdfFile.name}__${f.pdfFile.size}`);
       if (f.xmlFile) factFiles.add(`${f.xmlFile.name}__${f.xmlFile.size}`);
    });

    const dupes = (formData.adjuntos || []).filter(a => factFiles.has(`${a.file.name}__${a.file.size}`));
    if (dupes.length > 0) {
      notify(`Archivo duplicado: "${dupes[0].file.name}" ya se encuentra adjunto en la sección de Facturas. Quítalo de la lista general de Adjuntos.`, 'warning');
      return;
    }

    const maxMontoRaw = maxTotalOC;
    
    const schema = yup.object().shape({
      TOTALOC: reclassificationOnly ? yup.mixed().notRequired() : yup.number()
        .transform((value, originalValue) => {
          if (typeof originalValue === 'string') {
             const clean = originalValue.replace(/[^0-9.-]/g, '');
             return parseFloat(clean);
          }
          return value;
        })
        .required('El monto debe ser un número válido mayor a 0')
        .typeError('El monto debe ser numérico')
        .positive('El monto debe ser un número válido mayor a 0')
        .max(maxMontoRaw, `No puedes poner una cantidad mayor a ${formatCurrency(maxMontoRaw)}`),
        
      fechaVencimientoPrecio: reclassificationOnly ? yup.mixed().notRequired() : yup.string().required('Fecha de vencimiento es requerida'),

      tipoPago: reclassificationOnly ? yup.mixed().notRequired() : yup.string().required('Debes seleccionar la modalidad de pago (Contado o Crédito)'),

      fechaCompromiso: yup.mixed().test('fecha-compromiso-req', 'La Fecha compromiso de pago es obligatoria cuando seleccionas Crédito', function(val) {
        if (reclassificationOnly) return true;
        if (this.parent.tipoPago === 'CREDITO' && !val) {
          return false;
        }
        return true;
      }),
      
      caratulaBancaria: yup.mixed().test('has-caratula', 'La Carátula Bancaria es obligatoria. Sube el archivo en Adjuntos y etiquétalo como "Datos Bancarios".', () => {
        return (formData.adjuntos || []).some(a => a.type === 'CARATULA' || a.type === 'DATOS BANCARIOS');
      }),
      
      ordenCompraAdjunto: yup.mixed().test('has-oc', 'La Orden de Compra es obligatoria. Sube el archivo en Adjuntos y etiquétalo como "Orden de Compra".', () => {
        return (formData.adjuntos || []).some(a => a.type === 'OC');
      }),
      
      adjuntos: yup.array()
        .min(1, 'Debe adjuntar al menos un archivo')
        .test('no-zero-size', 'Hay adjuntos en 0 KB. Extrae el .rar/.zip a una carpeta y vuelve a adjuntar.', arr => {
          if (!arr) return false;
          return !arr.some(a => !a || !a.file || (typeof a.file.size === 'number' && a.file.size === 0));
        }),
        
      documentos: yup.object()
        .test('has-docs', 'Debe seleccionar al menos un documento', obj => Object.values(obj || {}).some(v => v))
        .test('facturas-docs-req', 'Debe incluir Factura.', obj => {
          if (!requiereFacturaAntesPago) return true;
          return Boolean(obj?.facturaa);
        }),
        
      facturasSeleccionadas: yup.array().test('facturas-array-req', 'Debes seleccionar al menos una factura.', function(val) {
        if (!requiereFacturaAntesPago) return true;
        if (!val || val.length === 0) return this.createError({ message: 'Debes seleccionar al menos una factura.', path: 'facturasSeleccionadas' });
        
        val.forEach(f => {
          if (!f?.uuid) return;
          if (!f.pdfFile) {
            errs[`facturaPdf__${f.uuid}`] = `PDF obligatorio para factura ${f.identificador || ''}`;
          } else if (f.pdfFile.size === 0) {
            errs[`facturaPdf__${f.uuid}`] = `El PDF (${f.identificador || ''}) viene en 0 KB`;
          }
          
          if (f.xmlFile && f.xmlFile.size === 0) {
            errs[`facturaXml__${f.uuid}`] = `El XML (${f.identificador || ''}) viene en 0 KB`;
          }
        });
        
        return true;
      })
    });

    try {
      await schema.validate(formData, { abortEarly: false });
    } catch (yupError) {
      if (yupError.inner) {
        yupError.inner.forEach(e => {
          if (!errs[e.path]) errs[e.path] = e.message;
        });
      }
      setCombinedErrors(errs);
      return;
    }
    
    if (Object.keys(errs).length > 0) {
      setCombinedErrors(errs);
      return;
    }

    const sourceRow = activeRowData || rowData;
    if (!sourceRow) return;

    setLoading(true);
    setSpinnerMessage(reclassificationOnly ? 'Actualizando clasificación...' : 'Procesando solicitud de pago...');

    if (reclassificationOnly) {
      const formDataToSend = new FormData();
      formDataToSend.append('AUTSOLPAGOID', sourceRow.AUTSOLPAGOID || sourceRow.ID || '');
      formDataToSend.append('documentos', JSON.stringify(formData.documentos));
      formDataToSend.append('FOLIO', sourceRow.FOLIO || sourceRow.FOLIO_ORDENCOMPRA || '');
      formDataToSend.append('PROVEEDOR', sourceRow.PROVEEDOR || sourceRow.proveedorNombre || '');
      formDataToSend.append('NOMBREEMPRESA', sourceRow.EMPRESA || '');
      
      const existingFilesToPreserve = [];
      formData.adjuntos.forEach((adjObj) => {
        if (adjObj.file?.serverSide && adjObj.file?.id) {
          existingFilesToPreserve.push({ 
              id: adjObj.file.id, 
              category: adjObj.type === 'CARATULA' ? 'DATOS BANCARIOS' : (adjObj.type === 'OC' ? 'OC' : (adjObj.type === 'FACTURA' ? 'FACTURA' : 'ADJUNTO'))
          });
        } else if (adjObj.type === 'CARATULA' || adjObj.type === 'DATOS BANCARIOS') {
          formDataToSend.append('caratulaBancaria', adjObj.file);
        } else if (adjObj.type === 'OC') {
          formDataToSend.append('ordenCompra', adjObj.file);
        } else if (adjObj.type === 'FACTURA') {
          formDataToSend.append('facturaPdf__new', adjObj.file);
        } else {
          formDataToSend.append('adjunto', adjObj.file);
        }
      });

      if (existingFilesToPreserve.length > 0) {
        formDataToSend.append('EXISTING_FILES', JSON.stringify(existingFilesToPreserve));
      }

      try {
        await reclasificarSolicitud(formDataToSend);
        setLoading(false);
        handleDialogClose();
        await handleOpenSweetAlert('La clasificación ha sido actualizada con éxito.', 'success');
        if (typeof onSaved === 'function') {
          onSaved();
        }
      } catch (err) {
        setLoading(false);
        await handleOpenSweetAlert(err.response?.data?.message || err.message || 'Error al actualizar clasificación', 'error');
      } finally {
        setLoading(false);
      }
      return;
    }

    setSpinnerMessage('Procesando solicitud de pago...');

    const facturasMeta = (formData.facturasSeleccionadas || []).map((f) => ({
      uuid: f.uuid,
      identificador: f.identificador || '',
      folio: f.folioERP || f.folio || '',
      serie: f.serieERP || f.serie || '',
      fecha: f.fecha,
    }));

    const dataToSend = {
      AUTSOLPAGOID: sourceRow.AUTSOLPAGOID || sourceRow.ID || '',
      COMPRADOR: sourceRow.COMPRADOR || sourceRow.SOLICITANTE,
      TOTALOC: parseFloat(formData.TOTALOC.replace(/[^0-9.-]/g, '')),
      FACTURA: facturasMeta?.[0]?.identificador || (formData.FACTURA || ''),
      FECHA: facturasMeta?.[0]?.fecha || (formData.FECHAEMISION || ''),
      observaciones: formData.observaciones,
      CONCEPTOPAGO: formData.conceptoPago,
      FOLIO: sourceRow.FOLIO || sourceRow.FOLIO_ORDENCOMPRA,
      ESTATUS: 3, // Pasa directo a Autorizada (sin necesidad de cadena de autorización)
      DIVISA: sourceRow.DIVISA || sourceRow.moneda || '',
      SESSION: loggedInUsername,
      CENTCOSTO: sourceRow.NOMBRE_CC || sourceRow.CENTRO_COSTOS || sourceRow.CENTROCOSTOS || sourceRow.CENTROCOSTO || sourceRow.NUMEROCC || '',
      DEPART: sourceRow.DEPARTAMENTO || '',
      documentos: JSON.stringify(formData.documentos),
      PROVEEDOR: sourceRow.PROVEEDOR || sourceRow.proveedorNombre || '',
      RFC: sourceRow.RFC || formData.proveedorRfc || '',
      FECHAVENCIMIENTO: formData.fechaVencimientoPrecio,
      NOMBREEMPRESA: sourceRow.EMPRESA || '',
      NUMERO_ECONOMICO: formData.numeroEconomico || '',
      CONDICIONES_CREDITO: condicionCreditoRaw,
      FACTURA_ANTES_PAGO: Number(requiereFacturaAntesPago),
      FACTURAS_META: JSON.stringify(facturasMeta),
      TIPO_PAGO: formData.tipoPago || '',
      FECHA_COMPROMISO: formData.tipoPago === 'CREDITO' ? (formData.fechaCompromiso || '') : '',
      // Flag de envío por corrección
      IS_CORRECTION: (guidedMode || isResend) ? 1 : 0,
      ORIGINAL_ID: sourceRow.AUTSOLPAGOID || sourceRow.ID || ''
    };

    const formDataToSend = new FormData();
    for (const key in dataToSend) {
      formDataToSend.append(key, dataToSend[key]);
    }

    for (const f of (formData.facturasSeleccionadas || [])) {
      if (!f?.uuid) continue;
      if (f.pdfFile) formDataToSend.append(`facturaPdf__${f.uuid}`, f.pdfFile);
      if (f.xmlFile) formDataToSend.append(`facturaXml__${f.uuid}`, f.xmlFile);
    }

    const existingFilesToPreserve = [];

    formData.adjuntos.forEach((adjObj) => {
      if (adjObj.file?.serverSide && adjObj.file?.id) {
        // En corrección, mandamos el ID y la categoría (posible nueva)
        existingFilesToPreserve.push({ 
            id: adjObj.file.id, 
            category: adjObj.type === 'CARATULA' ? 'DATOS BANCARIOS' : adjObj.type 
        });
      } else if (adjObj.type === 'CARATULA' || adjObj.type === 'DATOS BANCARIOS') {
        formDataToSend.append('caratulaBancaria', adjObj.file);
      } else if (adjObj.type === 'OC') {
        formDataToSend.append('ordenCompra', adjObj.file);
      } else if (adjObj.type === 'FACTURA') {
        formDataToSend.append('facturaPdf__new', adjObj.file);
      } else {
        formDataToSend.append('adjunto', adjObj.file);
      }
    });

    if (existingFilesToPreserve.length > 0) {
      formDataToSend.append('EXISTING_FILES', JSON.stringify(existingFilesToPreserve));
    }

    try {
      const response = await guardarSolicitudPago(formDataToSend);
      setLoading(false);
      handleDialogClose();
      if (response && response.excelUrl) {
        const modalHtml = `
          <div style="font-family: inherit; font-size: clamp(0.82rem, 0.78rem + 0.15vw, 0.92rem); text-align: center; color: #475569; padding: 0 4px;">
            <p style="margin: 0 0 clamp(10px, 1.5vw, 14px) 0; line-height: 1.5; font-weight: 500;">
              Tu solicitud de pago ha sido procesada con éxito y enviada al autorizador por correo.
            </p>
            
            <div style="display: flex; align-items: center; justify-content: space-between; gap: 10px; border: 1px solid #e2e8f0; border-radius: 12px; padding: clamp(8px, 1vw, 12px) clamp(10px, 1.4vw, 16px); background-color: #f8fafc; margin: clamp(10px, 1.5vw, 16px) 0; text-align: left; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);">
              <div style="display: flex; align-items: center; gap: clamp(8px, 1vw, 12px); min-width: 0;">
                <div style="width: 36px; height: 36px; border-radius: 8px; background: #ecfdf5; border: 1px solid #a7f3d0; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0;">
                  📊
                </div>
                <div style="min-width: 0;">
                  <div style="font-weight: 800; color: #0f172a; font-size: clamp(0.78rem, 0.74rem + 0.1vw, 0.86rem); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                    Solicitud_Pago.xlsx
                  </div>
                  <div style="font-size: clamp(0.66rem, 0.64rem + 0.05vw, 0.72rem); color: #64748b; font-weight: 600;">
                    Archivo Excel generado
                  </div>
                </div>
              </div>
              
              <a href="${response.excelUrl}" target="_blank" download style="display: inline-flex; align-items: center; gap: 6px; background: linear-gradient(135deg, #0655A5 0%, #0284c7 100%); color: white; padding: clamp(6px, 0.8vw, 8px) clamp(12px, 1.5vw, 16px); text-decoration: none; border-radius: 8px; font-weight: 700; font-size: clamp(0.72rem, 0.7rem + 0.08vw, 0.78rem); transition: all 0.15s ease; flex-shrink: 0; box-shadow: 0 4px 10px rgba(6, 85, 165, 0.25);">
                <span>📥</span> Descargar
              </a>
            </div>
            
            <p style="font-size: clamp(0.68rem, 0.65rem + 0.06vw, 0.74rem); color: #64748b; margin: clamp(6px, 1vw, 10px) 0 0 0; font-style: italic; font-weight: 500;">
              También se ha adjuntado este archivo al correo de autorización.
            </p>
          </div>
        `;
        await handleOpenSweetAlert(modalHtml, 'success', { useHtml: true, confirmButtonText: 'Aceptar' });
      } else {
        await handleOpenSweetAlert('Tu solicitud de pago ha sido procesada con éxito y enviada al autorizador por correo.', 'success');
      }
      if (typeof onSaved === 'function') {
        onSaved();
      }
    } catch (error) {
      setLoading(false);
      const resp = error.response;
      const data = resp?.data;
      let backendMessage;
      if (data) {
        if (typeof data === 'string') backendMessage = data;
        else if (typeof data === 'object') backendMessage = data.error || data.message || data.sqlMessage || null;
      }
      const errorMessage = backendMessage || error.message || 'La solicitud no pudo ser procesada, por favor intenta de nuevo';
      await handleOpenSweetAlert(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [
    formData, rowData, activeRowData, maxTotalOC, loggedInUsername, handleDialogClose, formatCurrency,
    condicionCreditoRaw, requiereFacturaAntesPago, ocEnCero, notify, onSaved,
    guidedMode, correctionMetadata
  ]);

  return {
    formData, setFormData,
    maxTotalOC, formattedTotalOC, setFormattedTotalOC,
    combinedErrors, setCombinedErrors, totalOCError, setTotalOCError,
    loading, spinnerMessage, updatingOC, numeroEconomicoBloqueado,
    facturaOptionsRemote, loadingFacturas, ocEnCero, snackbar, setSnackbar,
    notify, condicionCreditoRaw, monedaTipo, requiereFacturaAntesPago, setRequiereFacturaAntesPago,
    configNotificarAutorizador,
    handleActualizarOC, handleConfirmGuardar, handleDialogClose, handleTogglePreferenciaProveedor,
    formatCurrency, parseCurrency, handleFileChange, handleDrop, handleRemoveFile, handleUpdateFileType, handleCheckboxChange
  };
};

export { documentos };
